/**
 * Deterministic GTIN normalization (Phase Wellspring W1; widened Phase Confluence C3).
 *
 * TWO forms from ONE validity decision:
 *   • NATURAL store-form (`normalizeUpc`) — the validated retail code stored in products.upc +
 *     handed to Keepa: UPC-12 or EAN-13. Sheets carry codes dirtily (hyphenated, 11-digit
 *     truncations), so it strips non-digits, pads 11→12, and accepts 12 or 13 digits. Invalid
 *     inputs are preserved raw (never corrupted), `valid=false`. (C3 widened this from 12-only;
 *     the sole behavioral change is 13-digit EANs becoming valid.)
 *   • CANONICAL GTIN-14 key-form (`normalizeGtin`) — left-zero-pads the natural {12,13} to 14 so a
 *     UPC-12 and the EAN-13 of the same code collapse to ONE dedup key (GS1). DERIVED from the
 *     natural output, so the two can never disagree on validity. IN-MEMORY KEY ONLY — never stored,
 *     never passed to an external API (de-canonicalizing a 14 back to natural is lossy; products.upc
 *     stays natural — C3 D2).
 *
 * Pure — no I/O, ZERO imports (so the supabase/functions/_shared re-export shim bundles it for Deno).
 * `normalizeUpc` is applied inside `mapRowToProduct`'s `upc` coercion so the value feeding dedup keys
 * + the W2 lookup is the normalized one.
 */

export interface UpcNormalizeResult {
  /** Natural validated retail code (12 or 13 digits) when valid; the trimmed raw input when not. */
  value: string;
  /** True iff the input normalized to a valid natural retail code (12 or 13 digits). */
  valid: boolean;
  /** The original input, trimmed (for provenance / hold-pile visibility). */
  raw: string;
}

/**
 * Normalize a raw code cell to the NATURAL validated retail form: strip non-digits, pad an 11-digit
 * value to 12 with a leading zero, and accept exactly 12 (UPC-A) or 13 (EAN-13) digits. Invalid
 * inputs (anything not reducing to 12/13) return the trimmed raw value unchanged with `valid=false`
 * (preserve, don't corrupt). C3 widened from 12-only — the only behavioral change is 13-digit EANs.
 */
export function normalizeUpc(input: string | null | undefined): UpcNormalizeResult {
  const raw = String(input ?? '').trim();
  let digits = raw.replace(/\D/g, '');
  // 11-digit -> pad to 12 with a leading zero (common supplier truncation).
  if (digits.length === 11) digits = `0${digits}`;
  // Natural validated retail code: UPC-12 or EAN-13 (C3 — widened from 12-only).
  const valid = digits.length === 12 || digits.length === 13;
  return { value: valid ? digits : raw, valid, raw };
}

/** True iff `value` is already a normalized clean retail code (12 or 13 digits). C3: widened from 12-only. */
export function isNormalizedUpc(value: string | null | undefined): boolean {
  return typeof value === 'string' && /^\d{12,13}$/.test(value);
}

/**
 * The canonical GTIN-14 dedup KEY for a raw code — DERIVED from the natural validated form
 * (`normalizeUpc`) then left-zero-padded to 14, so a UPC-12 and the EAN-13 of the same code collapse
 * to ONE key. Returns `null` for anything that does not reduce to a valid 12/13 natural: multi-value
 * pollution (24/36-digit bracketed strings), <12, >13, and 14-digit ITF-14 carton codes (out of the
 * V1 retail-unit domain — never engineered for). IN-MEMORY KEY ONLY (C3 D2) — never stored, never
 * handed to Keepa.
 */
export function normalizeGtin(input: string | null | undefined): string | null {
  const { value, valid } = normalizeUpc(input);
  return valid ? value.padStart(14, '0') : null;
}
